import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import type { OverlayScrollbars, PartialOptions, EventListeners } from 'overlayscrollbars';
import { useMiddleButtonPanScroll } from '@/hooks/useMiddleButtonPanScroll';
import './OverlayScrollArea.css';

export const TRACKER_OVERLAY_SCROLL_OPTIONS: PartialOptions = {
  overflow: { x: 'hidden', y: 'scroll' },
  scrollbars: {
    theme: 'os-theme-tracker',
    visibility: 'auto',
    autoHide: 'scroll',
    autoHideDelay: 450,
    autoHideSuspend: false,
    dragScroll: true,
    clickScroll: true,
  },
};

function mergeOsOptions(base: PartialOptions, extra?: PartialOptions | null): PartialOptions {
  if (!extra) return base;
  return {
    ...base,
    ...extra,
    overflow: { ...base.overflow, ...extra.overflow },
    scrollbars: { ...base.scrollbars, ...extra.scrollbars },
  };
}

type RefLike<T> = React.RefObject<T | null> | React.MutableRefObject<T | null>;

export type OverlayScrollAreaProps = {
  element?: 'div' | 'ul' | 'main' | 'section' | 'aside';
  osOptions?: PartialOptions | null;
  /** Контейнер с контентом (прямые дети — как у обычного div/ul). Для scrollIntoView по li и т.п. */
  contentRef?: RefLike<HTMLElement> | null;
  /** Корневой хост OverlayScrollbars — для getBoundingClientRect / usePopoverPositioning. */
  hostRefForPosition?: RefLike<HTMLElement> | null;
  /** Viewport — элемент с реальным scrollTop / прокруткой (не host). */
  viewportRef?: RefLike<HTMLElement> | null;
  /** Смена viewport (инициализация / destroy) — для виртуализаторов с scroll на viewport OS. */
  onViewportElement?: ((el: HTMLElement | null) => void) | null;
  /** Инициализация без idle-defer (нужно, чтобы ref viewport был доступен сразу для scrollTo). */
  defer?: boolean;
  /**
   * Пересчитывать прокрутку, когда меняется высота прямых детей. OverlayScrollbars сам замечает только
   * изменения разметки, а рост глубоко внутри постоянных детей (догрузка данных, CSS-переход раскрытия)
   * пропускает: область остаётся в «прокручивать нечего» (overflow: hidden), колесо не работает.
   */
  updateOnContentResize?: boolean;
  children?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, 'ref'>;

function setRef<T>(r: RefLike<T> | null | undefined, value: T | null) {
  if (!r) return;
  (r as React.MutableRefObject<T | null>).current = value;
}

/** Следит за высотой прямых детей content (и за появлением новых) и пересчитывает OverlayScrollbars раз в кадр. */
function observeContentResize(instance: OverlayScrollbars): () => void {
  const { content } = instance.elements();
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      instance.update(true);
    });
  };
  const resize = new ResizeObserver(schedule);
  const observeChildren = () => {
    for (const child of Array.from(content.children)) resize.observe(child);
  };
  observeChildren();
  const mutation = new MutationObserver(observeChildren);
  mutation.observe(content, { childList: true });
  return () => {
    mutation.disconnect();
    resize.disconnect();
    if (frame) cancelAnimationFrame(frame);
  };
}

/**
 * Оверлейные скроллбары (OverlayScrollbars): не сдвигают контент, автоскрытие.
 */
export function OverlayScrollArea({
  element = 'div',
  className,
  style,
  children,
  osOptions,
  contentRef,
  hostRefForPosition,
  viewportRef,
  onViewportElement,
  defer = true,
  updateOnContentResize = false,
  onScroll,
  ...rest
}: OverlayScrollAreaProps) {
  const panViewportRef = useRef<HTMLElement | null>(null);
  const [panScrollReady, setPanScrollReady] = useState(false);
  const stopContentResizeRef = useRef<(() => void) | null>(null);

  const stopContentResize = useCallback(() => {
    stopContentResizeRef.current?.();
    stopContentResizeRef.current = null;
  }, []);
  useEffect(() => stopContentResize, [stopContentResize]);

  const syncRefs = useCallback(
    (instance: OverlayScrollbars | null) => {
      if (!instance) {
        setRef(contentRef, null);
        setRef(hostRefForPosition, null);
        setRef(viewportRef, null);
        panViewportRef.current = null;
        setPanScrollReady(false);
        onViewportElement?.(null);
        return;
      }
      const { content, host, viewport } = instance.elements();
      setRef(contentRef, content);
      setRef(hostRefForPosition, host);
      setRef(viewportRef, viewport);
      panViewportRef.current = viewport;
      setPanScrollReady(true);
      onViewportElement?.(viewport);
    },
    [contentRef, hostRefForPosition, viewportRef, onViewportElement],
  );

  useMiddleButtonPanScroll(panViewportRef, panScrollReady);

  const events = useMemo((): EventListeners => {
    const base: EventListeners = {
      initialized: (instance: OverlayScrollbars) => {
        syncRefs(instance);
        if (updateOnContentResize) {
          stopContentResize();
          stopContentResizeRef.current = observeContentResize(instance);
        }
      },
      updated: (instance: OverlayScrollbars) => syncRefs(instance),
      destroyed: () => {
        stopContentResize();
        syncRefs(null);
      },
    };
    if (onScroll) {
      base.scroll = (_instance, evt) => {
        onScroll(evt as unknown as React.UIEvent<HTMLDivElement>);
      };
    }
    return base;
  }, [syncRefs, onScroll, updateOnContentResize, stopContentResize]);

  const options = useMemo(
    () => mergeOsOptions(TRACKER_OVERLAY_SCROLL_OPTIONS, osOptions ?? undefined),
    [osOptions],
  );

  return (
    <OverlayScrollbarsComponent
      element={element}
      className={className}
      style={style}
      options={options}
      events={events}
      defer={defer}
      {...rest}
    >
      {children}
    </OverlayScrollbarsComponent>
  );
}
